import type { EffectDefinition, EffectContext } from './types'

interface Spark {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  brightness: number
}

interface SparkState {
  sparks: Spark[]
  nextBurst: number
}

function createBurst(x: number, y: number, count: number): Spark[] {
  const sparks: Spark[] = []
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2
    const speed = 80 + Math.random() * 250
    const life = 0.3 + Math.random() * 0.8
    sparks.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life,
      maxLife: life,
      size: 1 + Math.random() * 2,
      brightness: 0.6 + Math.random() * 0.4
    })
  }
  return sparks
}

export const sparks: EffectDefinition = {
  id: 'sparks',
  name: 'Sparks',

  init(ctx: EffectContext): SparkState {
    return {
      sparks: [],
      nextBurst: 0.5
    }
  },

  update(state: unknown, ctx: EffectContext) {
    const s = state as SparkState
    const dt = ctx.deltaTime / 1000

    // Spawn bursts
    s.nextBurst -= dt
    if (s.nextBurst <= 0) {
      const x = Math.random() * ctx.width
      const y = Math.random() * ctx.height
      const count = 5 + Math.floor(Math.random() * 15)
      s.sparks.push(...createBurst(x, y, count))
      s.nextBurst = 0.2 + Math.random() * 0.8
    }

    // Update sparks
    for (let i = s.sparks.length - 1; i >= 0; i--) {
      const spark = s.sparks[i]!
      spark.x += spark.vx * dt
      spark.y += spark.vy * dt
      spark.vy += 120 * dt // gravity
      spark.vx *= 0.98
      spark.life -= dt
      if (spark.life <= 0) s.sparks.splice(i, 1)
    }

    // Cap max particles
    if (s.sparks.length > 500) {
      s.sparks.splice(0, s.sparks.length - 500)
    }
  },

  draw(state: unknown, ctx: EffectContext) {
    const s = state as SparkState
    const { ctx: c } = ctx

    for (const spark of s.sparks) {
      const alpha = (spark.life / spark.maxLife) * spark.brightness * 0.7
      const warm = spark.life / spark.maxLife

      // Color shifts from white-yellow to orange as it dies
      const r = 255
      const g = Math.floor(200 + warm * 55)
      const b = Math.floor(warm * 180)

      // Trail
      const tailX = spark.x - spark.vx * 0.02
      const tailY = spark.y - spark.vy * 0.02
      c.beginPath()
      c.moveTo(tailX, tailY)
      c.lineTo(spark.x, spark.y)
      c.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.5})`
      c.lineWidth = spark.size * 0.5
      c.stroke()

      // Head
      c.beginPath()
      c.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2)
      c.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
      c.fill()
    }
  }
}
